import { AnalyticsContext } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useContext } from 'react';
import { AutoPopulateAccommodationPlacesPayloadV4, AutoPopulateAccommodationPlacesV4Mutation, useAutoPopulateAccommodationPlacesV4Mutation } from '@graphql/generated';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';
import { useLocaleContext } from '@localization/context/LocaleContext';
import type { CallAutoPopulateAccommodationPlacesV4Payload, AutoPopulationApiV4Response } from '../types';

const OPERATION_NAME = 'autoPopulationApiV4';

/**
 * Custom hook to call the auto-populate accommodation places v4 mutation.
 * @param options - Optional options.
 * @returns A tuple containing the callAutoPopulationApiV4 function and the loading state.
 */
export const useAutoPopulationApiV4 = () => {
  const analytics = useContext(AnalyticsContext);
  const { language: locale } = useLocaleContext();

  const [autoPopulateAccommodationPlacesV4, { loading: loading }] = useAutoPopulateAccommodationPlacesV4Mutation();

  const extractAutoPopulatedPlaces = useEventCallback((data: AutoPopulateAccommodationPlacesV4Mutation): ReadonlyArray<AutoPopulationApiV4Response> => {
    return data.autoPopulateAccommodationPlacesV4.map(place => ({
      accommodationPlaceId: place.accommodationPlaceId,
      whyThisHotelIsAGreatPick: place.whyThisHotelIsAGreatPick ?? null,
      proposedNoteFromHostToGuest: place.proposedNoteFromHostToGuest ?? null,
    }));
  });

  const callAutoPopulationApiV4 = useEventCallback(async (payloadArgs: CallAutoPopulateAccommodationPlacesV4Payload): Promise<ReadonlyArray<AutoPopulationApiV4Response>> => {
    const { eventId, location, scoringPriorityFields, context, searchRadiusKm } = payloadArgs;

    const payload: AutoPopulateAccommodationPlacesPayloadV4 = {
      eventId: eventId!,
      force: true,
      location,
      searchRadiusKm,
      locale,
      scoringPriorityFields: [...scoringPriorityFields],
    };

    if (!eventId) {
      const error = new Error('Missing event id');
      addError(error, {
        context,
        eventId: payload.eventId,
        latitude: payload.location?.latitude,
        longitude: payload.location?.longitude,
        isAsync: false,
        operation: OPERATION_NAME,
      });
      throw error;
    }

    try {
      const response = await autoPopulateAccommodationPlacesV4({ variables: { payload } });

      if (response.errors && response.errors.length > 0) {
        const error = Array.isArray(response.errors) ? response.errors[0] : (response.errors as unknown as Error);
        throw error;
      }

      if (!response.data) {
        return [];
      }

      const autoPopulatedPlaces = extractAutoPopulatedPlaces(response.data);

      analytics.track({
        category: 'accommodations',
        action: `TravelButtonInteracted`,
        label: 'autoPopulateAccommodationPlaces',
        extraInfo: {
          name: `autoPopulateAccommodationPlaces`,
          eventId: payload.eventId,
          latitude: payload.location?.latitude,
          longitude: payload.location?.longitude,
          placesCount: autoPopulatedPlaces.length,
          isAsync: false,
          source: context,
        },
      });

      return autoPopulatedPlaces;
    } catch (error) {
      const { errorExplanation, statusCode, isTimeout } = parseNetworkErrorDetails(error);
      addError(errorExplanation, {
        context,
        eventId: payload.eventId,
        latitude: payload.location?.latitude,
        longitude: payload.location?.longitude,
        isAsync: false,
        statusCode,
        isTimeout,
        originalError: error,
        operation: OPERATION_NAME,
      });
      throw error;
    }
  });

  return { callAutoPopulationApiV4, loading };
};
